import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Edit3, Trash2, Power, PowerOff, Calendar, Hotel } from "lucide-react";
import { SiteStatsStrip } from "@/components/etablissement/SiteStatsStrip";
import { SiteHoursTable } from "@/components/etablissement/SiteHoursTable";
import { SiteCongesList } from "@/components/etablissement/SiteCongesList";
import { SiteFinanceList } from "@/components/etablissement/SiteFinanceList";
import { SiteSeasonPanel } from "@/components/etablissement/SiteSeasonPanel";
import { SiteTeamTab } from "./SiteTeamTab";
import { monthKey, monthLabel, shiftMonth } from "@/components/etablissement/shared";

export function SiteDetailSheet({ siteId, seasonalMode, unassigned, onClose, onChanged, onEdit }) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(monthKey());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data: d } = await api.get(`/chef/etablissements/${siteId}`, { params: { month } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [siteId, month]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const site = data?.site;
  return (
    <Sheet open={!!siteId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-0" data-testid="site-detail-sheet">
        {site && (
          <>
            <SheetHeader className="p-6 border-b border-[#DEE2E6] bg-[#F8F9FA] text-left">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#868E96] font-bold">{site.code} {site.is_siege ? "· Siège" : ""}</div>
                  <SheetTitle className="font-display text-3xl font-black tracking-tighter" data-testid="site-detail-title">{site.name}</SheetTitle>
                  <div className="text-xs text-[#495057] mt-1">{[site.adresse, [site.code_postal, site.ville].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "Adresse non renseignée"}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="site-month-prev" className="h-9 px-2"><ChevronLeft className="h-4 w-4" /></Button>
                  <div className="h-9 px-3 border border-[#DEE2E6] bg-white flex items-center text-xs font-semibold min-w-[150px] justify-center" data-testid="site-month-label">{monthLabel(month)}</div>
                  <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="site-month-next" className="h-9 px-2"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </SheetHeader>

            <div className="p-6 space-y-5">
              <SiteStatsStrip stats={data.stats} compact />

              <Tabs defaultValue="equipe">
                <TabsList className="w-full justify-start flex-wrap h-auto bg-transparent border-b border-[#DEE2E6] rounded-none p-0 gap-1">
                  {[["equipe", "Équipe"], ["planning", "Planning"], ["conges", "Congés"], ["finance", "Finance"], ["saison", "Saison"], ["infos", "Infos"]].map(([k, l]) => (
                    <TabsTrigger key={k} value={k} data-testid={`site-tab-${k}`}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] data-[state=active]:shadow-none text-xs uppercase tracking-[0.15em] font-bold px-3 py-2">
                      {l}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="equipe" className="pt-4">
                  <SiteTeamTab site={site} salaries={data.salaries} unassigned={unassigned}
                    onAssign={(sid) => run(() => api.post(`/chef/etablissements/${site.id}/salaries`, { salarie_id: sid }), "Salarié affecté")}
                    onUnassign={(sid) => run(() => api.delete(`/chef/etablissements/${site.id}/salaries/${sid}`), "Salarié retiré de l'établissement")}
                    onResponsable={(sid) => run(() => api.put(`/chef/etablissements/${site.id}/responsable`, { salarie_id: sid }), sid ? "Responsable nommé" : "Responsable retiré")} />
                </TabsContent>

                <TabsContent value="planning" className="pt-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm text-[#495057]">Heures travaillées planifiées en {monthLabel(month).toLowerCase()}.</div>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/chef/planning?site=${site.id}`)} data-testid="site-open-planning" className="h-9 border-[#0A0A0A]">
                      <Calendar className="h-3.5 w-3.5 mr-1" /> Ouvrir le planning filtré
                    </Button>
                  </div>
                  <SiteHoursTable salaries={data.salaries} responsableId={site.responsable_id} />
                  {data.units.length > 0 && (
                    <div className="text-xs text-[#495057] flex items-center gap-2 bg-[#F8F9FA] border border-[#DEE2E6] p-3" data-testid="site-units-hint">
                      <Hotel className="h-3.5 w-3.5" /> {data.units.length} unité{data.units.length > 1 ? "s" : ""} d'hébergement rattachée{data.units.length > 1 ? "s" : ""} · {data.stats.reservations_mois} réservation{data.stats.reservations_mois > 1 ? "s" : ""} ce mois.
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="conges" className="pt-4">
                  <SiteCongesList conges={data.conges} canDecide
                    onDecide={(id, status, commentaire) => run(() => api.post(`/chef/conges/${id}/action`, { status, commentaire }), status === "approved" ? "Congé validé" : "Congé refusé")} />
                </TabsContent>

                <TabsContent value="finance" className="pt-4">
                  <SiteFinanceList entries={data.finance} canEdit
                    onSave={(body) => run(() => api.post("/chef/finance", { ...body, site_id: site.id }), "CA enregistré")}
                    onDelete={(id) => run(() => api.delete(`/chef/finance/${id}`), "Entrée supprimée")} />
                </TabsContent>

                <TabsContent value="saison" className="pt-4">
                  <SiteSeasonPanel site={site} seasonStats={data.season_stats} seasonalMode={seasonalMode} canEdit
                    onSave={(periods) => run(() => api.put(`/chef/etablissements/${site.id}/opening-periods`, { periods }), "Périodes d'ouverture enregistrées")} />
                </TabsContent>

                <TabsContent value="infos" className="pt-4 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <Info label="Téléphone" value={site.telephone} />
                    <Info label="Email" value={site.email} />
                    <Info label="Horaires" value={site.horaires} />
                    <Info label="Visible sur le mini-site" value={site.public ? "Oui" : "Non"} />
                    <Info label="Créé le" value={site.created_at ? new Date(site.created_at).toLocaleDateString("fr-FR") : "—"} />
                    <Info label="Statut" value={site.active === false ? "Inactif" : "Actif"} />
                  </div>
                  <div className="flex gap-2 flex-wrap pt-2 border-t border-[#DEE2E6]">
                    <Button variant="outline" onClick={() => onEdit(site)} data-testid="site-edit-btn" className="border-[#0A0A0A]"><Edit3 className="h-4 w-4 mr-2" /> Modifier</Button>
                    <Button variant="outline" onClick={() => run(() => api.patch(`/chef/sites/${site.id}`, { active: site.active === false }), site.active === false ? "Établissement réactivé" : "Établissement désactivé")} data-testid="site-toggle-active">
                      {site.active === false ? <><Power className="h-4 w-4 mr-2" /> Réactiver</> : <><PowerOff className="h-4 w-4 mr-2" /> Désactiver</>}
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" data-testid="site-delete-btn" className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"
                      onClick={() => { if (window.confirm(`Supprimer « ${site.name} » ? Les salariés affectés seront désaffectés, le CA saisi sera rattaché au siège.`)) run(() => api.delete(`/chef/sites/${site.id}`).then(onClose), "Établissement supprimé"); }}>
                      <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-[#DEE2E6] p-3 bg-white">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">{label}</div>
      <div className="mt-1 font-semibold break-words">{value || "—"}</div>
    </div>
  );
}
